/**
 * AiUiKit — shared building blocks for the VD Manager AI Assistant.
 * Used by both the side-drawer (`AiAssistantDrawer.jsx`) and the full-page tab (`CrmAssistant.jsx`)
 * to keep the visual language perfectly consistent.
 *
 * Each primitive accepts an optional `light` prop:
 *   - false (default): dark mode styling — used by the drawer, which stays dark in every theme.
 *   - true: light mode styling — used by the tab AI Assistant when the app is in light theme,
 *     keeps pill colors semantic but with pale bg + dark text so contrast on white is high.
 */
import React from "react";
import { ArrowRight } from "lucide-react";

const PILL_TONES_DARK = {
    red: "bg-[#3B1414] text-[#FF6B6B] border border-[#FF4D4D]/40",
    blue: "bg-[#0F1E38] text-[#5FA7FF] border border-[#007BFF]/40",
    amber: "bg-[#2E1F0A] text-[#FBBF24] border border-[#F59E0B]/40",
    green: "bg-[#0E2617] text-[#4ADE80] border border-[#22C55E]/40",
    purple: "bg-[#1E163A] text-[#C084FC] border border-[#8B5CF6]/40",
    gray: "bg-white/5 text-ink-300 border border-white/10",
};

const PILL_TONES_LIGHT = {
    red: "bg-red-50 text-red-700 border border-red-200",
    blue: "bg-blue-50 text-blue-700 border border-blue-200",
    amber: "bg-amber-50 text-amber-800 border border-amber-200",
    green: "bg-green-50 text-green-700 border border-green-200",
    purple: "bg-purple-50 text-purple-700 border border-purple-200",
    gray: "bg-slate-100 text-slate-700 border border-slate-200",
};

/** Small pill badge with themed color. */
export function Pill({ tone = "gray", children, light = false }) {
    const table = light ? PILL_TONES_LIGHT : PILL_TONES_DARK;
    return (
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium ${table[tone] || table.gray}`}>
            {children}
        </span>
    );
}

/** Priority → pill tone mapping. */
export function priorityPill(priority) {
    if (priority === "alta") return { tone: "red", label: "Alta priorità" };
    if (priority === "media") return { tone: "blue", label: "Media" };
    return { tone: "gray", label: "Bassa" };
}

/** Deadline (days_left) → pill tone mapping for scadenze. */
export function deadlinePill(days) {
    if (days < 0) return { tone: "red", label: `Scaduto da ${-days} giorni` };
    if (days <= 7) return { tone: "red", label: `Scade tra ${days} giorni` };
    if (days <= 30) return { tone: "amber", label: `Scade tra ${days} giorni` };
    return { tone: "gray", label: `Scade tra ${days} giorni` };
}

/** Card section with title + optional subtitle + list rows + optional footer link. */
export function SectionCard({
    icon,
    iconClass,
    title,
    subtitle,
    children,
    footerLabel,
    onFooterClick,
    testid,
    dense = false,
    light = false,
}) {
    // Default icon color (blue) unless overridden by caller.
    const defaultIcon = light ? "text-[#1E40AF]" : "text-[#5FA7FF]";
    const iconCls = iconClass || defaultIcon;
    const borderCls = light ? "border-slate-200" : "border-white/8";
    const titleCls = light ? "text-slate-900" : "text-white";
    const subtitleCls = light ? "text-slate-600" : "text-ink-400";
    const footerBtnCls = light
        ? "text-slate-700 border border-slate-300 hover:border-slate-500 hover:bg-slate-50"
        : "text-ink-200 border border-white/15 hover:border-white/40 hover:bg-white/5";
    return (
        <div data-testid={testid} className={`${dense ? "pt-4" : "pt-6"} pb-2 border-t ${borderCls}`}>
            <div className="flex items-center gap-2.5 mb-1">
                <span className={iconCls}>{icon}</span>
                <h4 className={`text-[13px] uppercase tracking-[0.14em] font-semibold ${titleCls}`}>{title}</h4>
            </div>
            {subtitle && <p className={`text-[13px] ml-8 mb-4 ${subtitleCls}`}>{subtitle}</p>}
            <div className="space-y-1.5 ml-1">{children}</div>
            {footerLabel && (
                <button
                    data-testid={`${testid}-see-all`}
                    onClick={onFooterClick}
                    className={`mt-4 inline-flex items-center gap-2 px-4 py-2 text-[13px] rounded-lg transition-colors ${footerBtnCls}`}
                >
                    {footerLabel} <ArrowRight size={13} />
                </button>
            )}
        </div>
    );
}

/** Clickable row with a left icon and one/more pills on the right. */
export function Row({ icon, label, sub, pills = [], onClick, testid, light = false }) {
    const iconCls = light ? "text-[#1E40AF]" : "text-[#5FA7FF]";
    const rowHoverCls = light ? "hover:bg-slate-100 focus:bg-slate-200" : "hover:bg-white/5 focus:bg-white/8";
    const labelCls = light
        ? "text-slate-900 group-hover:text-[#1E40AF]"
        : "text-white group-hover:text-[#5FA7FF]";
    const subCls = light ? "text-slate-500" : "text-ink-400";
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left focus:outline-none transition-colors group ${rowHoverCls}`}
        >
            <span className={`shrink-0 ${iconCls}`}>{icon}</span>
            <span className="flex-1 min-w-0">
                <span className={`block text-[15px] truncate transition-colors ${labelCls}`}>{label}</span>
                {sub && <span className={`block text-[12px] truncate mt-0.5 ${subCls}`}>{sub}</span>}
            </span>
            <span className="flex items-center gap-2 shrink-0">
                {pills.map((p, i) => (
                    <Pill key={i} tone={p.tone} light={light}>{p.label}</Pill>
                ))}
            </span>
        </button>
    );
}
